import { useModal } from '@ebay/nice-modal-react';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Button from 'components/field/Button';

import useAlert from 'hooks/useAlert';
import useModalRouter from 'hooks/useModalRouter';

import { removeModal, updateModal } from 'stores/modalsSlice';

import { faDash, faXmark } from '@fortawesome/pro-solid-svg-icons';

import ReactPortal from './ReactPortal';
import * as S from './styled/Modal.styled';

/**
 * @author Burak Unsal
 * @description Modal component is a dialog window that is displayed on top of the current page.
 * @param {Object} props - props of the component.
 * @param {'xx-small' | 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' | 'no-max-width'} [props.maxWidth = 'large'] - max width of the modal.
 * @param {boolean} [props.fullwidth = false] - if true, modal will be full width.
 * @param {boolean} [props.closeButton = true] - if true, close button will be visible.
 * @param {boolean} [props.minimizeButton] - takes a function that returns a button. if given, minimize button will be visible.
 * @param {string} [props.id] - id of the wrapper container of the modal. prefix of the id will be 'modal-'.
 * @param {string} [props.name] - name of the modal. If name is given, modal will be visible when url has modal query param with the same name.
 * @param {Function} [props.onClose] - function to be called when close button is clicked.
 * @param {Function} [props.onMinimize] - function to be called when minimize button is clicked.
 * @param {string} [props.className] - class name of the wrapper container of the modal.
 * @param {Object} [props.style] - style of the wrapper container of the modal.
 * @param {string} [props.padding] - padding of the wrapper container of the modal.
 * @param {string} [props.position = "center"] - position of the wrapper container of the modal.
 * @param {boolean} [props.closeOnClickOutside = false] - if true, modal will be closed when clicked outside of the modal.
 * @param {JSX.Element} props.children - children of the component.
 * @returns {JSX.Element} - Modal component.
 * @example
 * <Modal maxWidth="x-large" fullwidth>
 * 	<ModalTitle>
 * 		<Typography variant="p1">createShipmentWithOtoRates</Typography>
 * 	</ModalTitle>
 * 	<ModalContent>
 * 	</ModalContent>
 * 	<ModalActions>
 * 		<Button label="createShipment" />
 * 	</ModalActions>
 * </Modal>
 *
 */

const Modal = ({
	id,
	children,
	name,
	onClose,
	onMinimize,
	maxWidth = 'large',
	fullwidth = false,
	closeButton = true,
	minimizeButton,
	className,
	style,
	padding,
	position = 'center',
	closeOnClickOutside = false,
	zIndex = 'var(--z-index-9)'
}) => {
	const modal = useModal();
	const { showModal, closeModal } = useModalRouter();
	const { showInfo } = useAlert();
	const [searchParams] = useSearchParams();
	const [isOpen, setOpen] = useState(false);
	const [modalParams, setModalParams] = useState(null);
	const modalRef = useRef(null);
	const dispatch = useDispatch();
	const modals = useSelector((state) => state.modals);
	const isVisible = modals[`${modalParams?.modal ?? ''}_${modalParams?.modalID ?? ''}`]?.visible;

	const handleCloseModal = async () => {
		if (onClose) {
			const resp = await onClose();
			if (resp === true) return;
		}

		if (name) {
			closeModal();
		} else {
			modal.resolve();
			modal.remove();
		}
	};

	const handleMinimizeModal = async () => {
		if (Object.keys(modals).length >= 6) {
			showInfo({
				header: 'maximumNumberOfMinimizedModalsReached',
				message: 'maximumNumberOfMinimizedModalsReachedMessage'
			});
			return;
		}

		onMinimize && (await onMinimize());

		dispatch(
			updateModal({
				...modalParams,
				visible: false
			})
		);

		if (name) {
			closeModal();
		}
	};

	useEffect(() => {
		const modalName = searchParams.get('modal');

		if (modalName && name && modalName === name && !isOpen) {
			const params = {};

			searchParams.forEach((value, key) => {
				if (key === 'modal' || key === 'modalID' || key === 'id' || key === 'ids' || key === 'mode') {
					params[key] = value;
				}
			});

			setModalParams(params);
			setOpen(true);
		}

		if (isOpen && modalName !== name && isVisible !== false) {
			dispatch(removeModal(modalParams));
			modal.resolve();
			modal.remove();
		}
	}, [searchParams]);

	const showMinimizedModal = () => {
		showModal(name, modalParams);
	};

	const closeMinimizedModal = () => {
		dispatch(removeModal(modalParams));
		modal.remove();
	};

	useEffect(() => {
		if (closeOnClickOutside) {
			const handleClickOutside = (event) => {
				if (modalRef.current && !modalRef.current.contains(event.target)) {
					handleCloseModal();
				}
			};

			document.addEventListener('mousedown', handleClickOutside);

			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [closeOnClickOutside]);

	return (
		<>
			{isVisible === false &&
				isOpen &&
				createPortal(
					minimizeButton(showMinimizedModal, closeMinimizedModal),
					document.getElementById('modal-manager')
				)}
			<ReactPortal wrapperId={`modal-${modal.id}`}>
				<S.Shadow
					className={
						{
							center: 'modal-center-shadow',
							left: 'modal-left-shadow',
							right: 'modal-right-shadow'
						}[position] ?? 'modal-center-shadow'
					}
					style={{
						visibility: isVisible === false ? 'hidden' : 'visible'
					}}
					zIndex={zIndex}>
					<S.Container
						ref={modalRef}
						id={`${id ?? ''} ${name ?? ''}`}
						fullwidth={fullwidth}
						className={`${maxWidth ?? ''} ${className ?? ''}`}
						style={style}>
						<S.Wrapper fullwidth={fullwidth} style={{ padding }}>
							{children}
						</S.Wrapper>
						{name && minimizeButton && (
							<Button
								className="minimizeBtn"
								variant="borderless"
								icon={faDash}
								onMouseDown={(event) => event.stopPropagation()}
								onTouchEnd={(event) => event.stopPropagation()}
								onClick={handleMinimizeModal}
								iconSize="medium"
								iconButton
							/>
						)}
						{closeButton && (
							<Button
								className="closeBtn"
								variant="borderless"
								icon={faXmark}
								onMouseDown={(event) => event.stopPropagation()}
								onTouchEnd={(event) => event.stopPropagation()}
								onClick={handleCloseModal}
								iconSize="medium"
								iconButton
							/>
						)}
					</S.Container>
				</S.Shadow>
			</ReactPortal>
		</>
	);
};

export default Modal;
